/**
 * Pure helpers for the review-screen line-item table (Stage-2 Unit 2).
 *
 * The line-item table renders a row's keys dynamically. These helpers are the
 * single source of truth for WHICH columns render, which are operator-editable,
 * and how a typed value is coerced before it goes to the Unit-1 PUT route. Kept
 * pure (no React, no DOM) so they are unit-testable under CRA's jest without RTL.
 */

/**
 * Keys never shown as columns in the line-item table. Structural / provenance /
 * identity columns plus the two Unit-1 additions (`line_number`, `extra`) that
 * would otherwise leak in as a raw int column and a `"{}"` JSON column.
 * `source` is excluded from display but still read per-row for the manual flag.
 */
export const LINE_ITEM_HIDDEN_COLUMNS = [
  'id',
  'capture_document_id',
  'tenant_id',
  'created_at',
  'updated_at',
  'sort_order',
  'page_index',
  'row_index',
  'line_number',
  'confidence',
  'source',
  'evidence',
  'extra',
  'extraction_method',
];

/**
 * Columns an operator may correct inline — exactly the Unit-1 PUT whitelist
 * (lineItemsRepo.EDITABLE_LINE_ITEM_COLUMNS). Anything else rendered (none today
 * after the hidden list) stays read-only.
 */
export const EDITABLE_LINE_ITEM_COLUMNS = [
  'sku',
  'qty',
  'description',
  'unit_price',
  'total_price',
  'currency',
];

/** Editable columns whose value is numeric (rendered as a number input). */
export const NUMERIC_LINE_ITEM_COLUMNS = ['qty', 'unit_price', 'total_price'];

/**
 * Visible columns for a set of rows, in the first row's key order, minus the
 * hidden set. Returns [] for an empty/invalid list.
 */
export function visibleLineItemColumns(rows) {
  if (!Array.isArray(rows) || rows.length === 0 || !rows[0]) return [];
  return Object.keys(rows[0]).filter((k) => !LINE_ITEM_HIDDEN_COLUMNS.includes(k));
}

/** True when a column is operator-correctable. */
export function isEditableLineItemColumn(key) {
  return EDITABLE_LINE_ITEM_COLUMNS.includes(key);
}

/** True when an editable column should be edited as a number. */
export function isNumericLineItemColumn(key) {
  return NUMERIC_LINE_ITEM_COLUMNS.includes(key);
}

/**
 * Coerce a raw input string into the value to PUT for `columnKey`.
 *
 * - Empty/whitespace-only → null (clears the cell; Unit-1 validateLineItemPatch
 *   allows null for scalars).
 * - Numeric columns: parse to Number; a non-empty, non-numeric value is INVALID
 *   (caller keeps the edit open and shows an inline error — never saves NaN).
 * - String columns: the trimmed string as-is.
 *
 * Returns { ok: true, value } or { ok: false, error }. Pure — no save side-effect.
 */
export function coerceLineItemValue(columnKey, rawInput) {
  const raw = rawInput == null ? '' : String(rawInput);
  const trimmed = raw.trim();

  if (trimmed === '') {
    return { ok: true, value: null };
  }

  if (isNumericLineItemColumn(columnKey)) {
    const n = Number(trimmed);
    if (!Number.isFinite(n)) {
      return { ok: false, error: 'Enter a valid number' };
    }
    return { ok: true, value: n };
  }

  return { ok: true, value: trimmed };
}

/**
 * Seed an <input> from a row's current cell value. null/undefined → '' so the
 * field starts empty (and a save of an untouched empty field is a no-op null).
 */
export function lineItemInputSeed(value) {
  if (value == null) return '';
  return String(value);
}

/**
 * True when any line item carries an operator correction (source='manual').
 *
 * Drives the warn-before-reextract guard (Stage-2 Unit 3): re-extraction
 * delete-all-then-rebuilds, silently discarding manual corrections, so we
 * confirm first when this is true. Safe on null/empty/rows-missing-source.
 *
 * Known limit (deferred): a doc whose only manual action was DELETING a row
 * leaves no surviving manual row, so this returns false for that case.
 */
export function hasManualLineItemCorrections(lineItems) {
  return Array.isArray(lineItems) && lineItems.some((li) => li && li.source === 'manual');
}
